/**
 * The debug data presets (`:~dbg:data:*`): the chart presets a host's debug
 * sheets use (scalars, sine waves, candles, the Whoop / Garmin / stock
 * demos). Built in (`SheetRegistries.BUILTINS` through `data-presets.ts`), so
 * every host and the CLI render them alike.
 */
import type { Option } from "../../core/option.ts";
import {
  NaiveDateTime,
  Time,
} from "@tento-chrono";
import type { DataPresetSpec } from "../../rows/data-preset-spec.ts";
import { BY_MSE, BY_DSE, mseDisplay, dseDisplay } from "./data-preset-axes.ts";

function parseDebugTimestamp(value: Option<string>): Option<NaiveDateTime> {
  if (value === null || value === undefined) return null;
  return NaiveDateTime.parse(value.replace(/Z$/, "")) ?? null;
}

export const DBG_DATA_PRESETS: ReadonlyMap<string, readonly DataPresetSpec[]> = new Map<string, readonly DataPresetSpec[]>([
// ─── Debug Data ──────────────────────────────────────────────────
[":~dbg:data:scalars", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 400, center: 200 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "numeric", radius: 3, color: "#999cff" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", key: "numeric", color: "#5158d5" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "numeric",
        // C1: the cell value IS the scalar — `entry` returns it directly.
        formula: `entry`,
        attributes: {
          source: ":~dbg:data:scalars",
          seriesKey: "numeric",
          seriesType: "regression",
          dataSource: "formula",
        },
      },
    ],
  },
]],

// Procedural-signal "debug" curve — the server-side mirror of the retired
// client `fakeChartData` (legacy `GraphData.Generated`): noisy points + a
// regression band/curve, rendered through the unified PlotFrame path.
[":~dbg:data:debug", [
  {
    type: "data-canvas",
    // Tall chart that subsumes the raw scalar cells (mirrors the stocks preset),
    // so the row renders the chart — not a JSON cell row + a squished strip.
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
      // No fixed yAxis — auto-fit the visible y-extent, matching the legacy
      // `GraphData.Generated` (which the retired fakeChartData curve used).
    },
    attributes: {
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      // Translucent red-above / blue-below fill (the "space above and below the
      // 2 lines", matching ui.lona.so #tab:chart); ±10% of the y-extent. Drawn
      // before band/curve so it reads as a background zone.
      { type: "canvas.regression-fill", attributes: { id: "fill", key: "default", offset: 0.1 } },
      { type: "canvas.regression-band", attributes: { id: "band", key: "default" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", key: "default", color: "#fe887c" } },
      // Nest the series under the canvas so the source row is SUBSUMED (one
      // chart row, not a raw-JSON row + a squished strip) — see graph-props
      // `propsFromNodes` + the stocks preset.
      {
        type: "data-series",
        label: "default",
        formula: `(json-get entry "v")`,
        attributes: {
          source: ":~dbg:data:debug",
          seriesKey: "default",
          seriesType: "regression",
          convHalfWidth: 5,
          dataSource: "formula",
        },
      },
    ],
  },
]],

[":~dbg:data:sine", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: -1.25, max: 1.25, center: 0 },
    },
    children: [
      { type: "canvas.line", attributes: { key: "default", color: "#2563eb" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "default",
        // C1: single-Y formula (scope row).
        formula: `(json-get entry "v")`,
        attributes: {
          source: ":~dbg:data:sine",
          seriesKey: "default",
          seriesType: "default",
          dataSource: "formula",
        },
      },
    ],
  },
]],

[":~dbg:data:sine-noise", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: -1.5, max: 1.5, center: 0 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band", key: "default" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", key: "default", color: "#fe887c" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "default",
        formula: `(json-get entry "v")`,
        attributes: {
          source: ":~dbg:data:sine-noise",
          seriesKey: "default",
          seriesType: "regression",
          convHalfWidth: 5,
          dataSource: "formula",
        },
      },
    ],
  },
]],

[":~dbg:data:sine-gappy", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: -1.5, max: 1.5, center: 0 },
    },
    children: [
      { type: "canvas.points", attributes: { key: "default", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-band", attributes: { id: "band", key: "default" } },
      { type: "canvas.regression-curve", attributes: { id: "curve", key: "default", color: "#fe887c" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "default",
        formula: `(json-get entry "v")`,
        attributes: {
          source: ":~dbg:data:sine-gappy",
          seriesKey: "default",
          seriesType: "regression",
          // Connected story: halfWidth (10) exceeds the ~8-day mid-month gap so
          // the kernel evaluates across it, and maxGap (60) keeps it one segment
          // — the curve bridges the gap smoothly instead of breaking into pieces.
          convHalfWidth: 10,
          maxGap: 60,
          dataSource: "formula",
        },
      },
    ],
  },
]],

[":~dbg:data:candles-15m", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      yAxis: { min: 90, max: 120, center: 105 },
    },
    children: [
      { type: "canvas.candlestick", attributes: { id: "candles", key: "candles" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "candles",
        // OHLC compound-Y via `hash-map` record + `shape:"ohlc"`. Entry
        // scope (DTR cells) — X = dtr.start.mse; the record carries the bar.
        // A missing/non-number field → record with a `null` → coerceY rejects
        // → point skipped, matching the closure's `return []`.
        formula:
          `(hash-map "open" (json-get entry "open") "high" (json-get entry "high") "low" (json-get entry "low") "close" (json-get entry "close"))`,
        attributes: {
          source: ":~dbg:data:candles-15m",
          seriesKey: "candles",
          seriesType: "default",
          dataSource: "formula",
          scope: "entry",
          shape: "ohlc",
        },
      },
    ],
  },
]],

[":~dbg:data:whoop-hrv", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_MSE,
      xDisplay: mseDisplay,
    },
    attributes: {
      xPadding: { days: 14 },
      yAxis: { min: 0, max: 200, center: 100 },
    },
    children: [
      { type: "canvas.points", attributes: { id: "recovery-points", key: "recovery", radius: 2, color: "#ffbab3" } },
      { type: "canvas.regression-curve", attributes: { id: "recovery-curve", key: "recovery", color: "#fe887c" } },
      { type: "canvas.points", attributes: { id: "hrv-points", key: "hrv", radius: 2, color: "#cbe9c8" } },
      { type: "canvas.regression-curve", attributes: { id: "hrv-curve", key: "hrv", color: "#51b748" } },
      // Both series nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "recovery",
        // timestamp-X formula. date.utc handles the trailing `Z`; its .mse
        // equals the closure's parseDebugTimestamp (strip-Z + NaiveDateTime.parse).
        formula:
          `(list (list (date.mse (date.utc (json-get entry "created_at"))) (json-get-in entry ["score" "recovery_score"])))`,
        attributes: {
          source: ":~dbg:data:whoop-hrv",
          seriesKey: "recovery",
          seriesType: "regression",
          dataSource: "formula",
          convHalfWidth: Time.MS_PER_DAY * 4,
        },
      },
      {
        type: "data-series",
        label: "hrv",
        formula:
          `(list (list (date.mse (date.utc (json-get entry "created_at"))) (json-get-in entry ["score" "hrv_rmssd_milli"])))`,
        attributes: {
          source: ":~dbg:data:whoop-hrv",
          seriesKey: "hrv",
          seriesType: "regression",
          yRange: { min: 0, max: 200, center: 100 },
          dataSource: "formula",
          convHalfWidth: Time.MS_PER_DAY * 4,
        },
      },
    ],
  },
]],

[":~dbg:data:garmin", [
  {
    type: "data-canvas",
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
    },
    attributes: {
      yAxis: { min: 0, max: 14_000, center: 7_000 },
    },
    children: [
      { type: "canvas.bar-chart-with-width", attributes: { id: "steps-bars", key: "steps", barColor: "#51b748" } },
      // Nested under the canvas (subsumes the source row) — see propsFromNodes.
      {
        type: "data-series",
        label: "steps",
        // bar-width compound-Y `[value, width]`. Row scope — X = the cell's
        // DSE; Y = `(list steps 0.8)`. `shape:"bar-width"` types the 2-list; a
        // non-number `steps` → `(list null 0.8)` → coerceY rejects → skipped,
        // matching the closure.
        formula: `(list (json-get entry "steps") 0.8)`,
        attributes: {
          source: ":~dbg:data:garmin",
          seriesKey: "steps",
          seriesType: "default",
          dataSource: "formula",
          shape: "bar-width",
        },
      },
    ],
  },
]],

[":~dbg:data:stocks", [
  {
    type: "data-canvas",
    // Tall chart on top; the data series is nested + subsumed beneath it.
    heightPx: 520,
    graph: {
      toXRange: BY_DSE,
      xDisplay: dseDisplay,
      // Fixed GLOBAL price band — the chart uses this as a stable y-range that
      // does not re-fit as the x-axis pans. Matches the bounded walk in the
      // `stock_cells` generator (lona-rows .../debug/data/mod.rs:stock_close,
      // close ∈ [86,134] + intrabar spread → high/low band ≈ [84,136]).
    },
    attributes: {
      yAxis: { min: 84, max: 136, center: 110 },
    },
    children: [
      {
        type: "data-series",
        label: "candles",
        // OHLC compound-Y. Row scope — X = the cell's day-serial (DSE,
        // the default xSource); Y = the `hash-map` OHLC record. `shape:"ohlc"`
        // types it.
        formula:
          `(hash-map "open" (json-get entry "open") "high" (json-get entry "high") "low" (json-get entry "low") "close" (json-get entry "close"))`,
        attributes: {
          source: ":~dbg:data:stocks",
          seriesKey: "candles",
          seriesType: "default",
          dataSource: "formula",
          shape: "ohlc",
          // The stocks provider emits daily OHLC; map each day to one candle
          // keyed by its day-serial (DSE) x so the candlestick renderer draws
          // one box+wick per day.
        },
        // The renderer is the series' own child so the data-canvas layout
        // subsumes the series (renderer-id prefix "canvas.*"): it feeds the
        // chart but does not render as its own row.
        children: [
          { type: "canvas.candlestick", attributes: { id: "candles", key: "candles" } },
        ],
      },
      {
        // Project a time-axis row to the sheet footer: the timeline-legend
        // renders per-column date ticks, which serve as the chart's x-axis.
        type: "projection",
        label: "Time",
        attributes: { surface: "footer", preset: "data-x-axis", visible: true },
        children: [
          { type: "timeline-legend", label: "Time" },
        ],
      },
    ],
  },
]],
]);
